import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * L-VEN.1 / L-DRV.1 (D-VEN.1): "mais comprados" do cliente — fonte ÚNICA para o seletor de produto
 * da web (ProductServiceCombobox) e do app do motorista.
 *
 * L-VEN.2 (L-VEN SQL-K): a fonte é a RPC get_customer_purchased_products, que conta nas VENDAS
 * (não mais a tabela customer_product_history). Sem productIds: os mais comprados; com productIds:
 * só os já comprados dentro da lista (grupo "Já comprados" da busca). Ranking vem ordenado do banco
 * (sale_count DESC, last_sale_date DESC, limit). Só produtos ativos (e, com availableForSaleOnly,
 * só os disponíveis para venda — espelha o filtro da lista de quem consome). Tenant: a RPC deriva do
 * caller; products filtra por company_id (RLS reforça).
 */

export const CUSTOMER_TOP_PRODUCTS_LIMIT = 5;

export interface CustomerTopProduct {
  id: string;
  name: string;
  sku: string | null;
  ean: string | null;
  sale_price: number | null;
  stock_quantity: number | null;
  rental_stock_quantity: number | null;
  reserved_stock_quantity: number | null;
  product_type: string | null;
  /** quantas vendas do cliente tiveram este produto (critério do ranking) */
  sale_count: number;
}

interface UseCustomerTopProductsOptions {
  /** true = só produtos com available_for_sale (listas do motorista que já filtram assim) */
  availableForSaleOnly?: boolean;
  /** L-VEN.2: restringe aos ids informados (já comprados dentro da busca); null/undefined = mais comprados */
  productIds?: string[] | null;
  /** L-VEN.2: termo da busca que originou productIds — entra na queryKey no lugar da lista de ids */
  cacheKey?: string;
  /** L-VEN.2: limite do ranking; default CUSTOMER_TOP_PRODUCTS_LIMIT */
  limit?: number;
  /** L-VEN.2: mesmo filtro de categoria da lista de quem consome */
  categoryFilter?: string[];
}

export function useCustomerTopProducts(
  companyId: string | null | undefined,
  customerId: string | null | undefined,
  enabled: boolean,
  options: UseCustomerTopProductsOptions = {},
) {
  const availableForSaleOnly = options.availableForSaleOnly === true;
  const limit = options.limit ?? CUSTOMER_TOP_PRODUCTS_LIMIT;

  return useQuery({
    // L-VEN.2: cacheKey (termo da busca) e limit na chave; a lista de ids nunca entra na chave
    queryKey: ['customer-top-products', companyId, customerId, availableForSaleOnly, options.cacheKey ?? null, limit],
    queryFn: async (): Promise<CustomerTopProduct[]> => {
      if (!companyId || !customerId) return [];

      // (a) ranking pelas vendas (RPC get_customer_purchased_products, guarda 91): sale_count DESC,
      // last_sale_date DESC, limit — já ordenado pelo banco. types.ts não regenerado: cast no nome.
      const { data: historyData, error: historyError } = await supabase.rpc(
        'get_customer_purchased_products' as any,
        {
          p_customer_id: customerId,
          p_product_ids: options.productIds ?? null,
          p_limit: limit,
        },
      );
      if (historyError) throw historyError;
      const history = (historyData ?? []) as { product_id: string; sale_count: number; last_sale_date: string | null }[];
      if (history.length === 0) return [];

      // (b) dados do produto (mesmas colunas das listas); reordenado pelo rank de (a)
      const ids = history.map(h => h.product_id);
      let productQuery = supabase
        .from('products')
        .select('id, name, sku, ean, sale_price, stock_quantity, rental_stock_quantity, reserved_stock_quantity, product_type')
        .eq('company_id', companyId)
        .eq('active', true)
        .in('id', ids);
      if (availableForSaleOnly) {
        productQuery = productQuery.eq('available_for_sale', true);
      }
      if (options.categoryFilter && options.categoryFilter.length > 0) {
        productQuery = productQuery.in('category', options.categoryFilter);
      }
      const { data: products, error: productsError } = await productQuery;
      if (productsError) throw productsError;

      const byId = new Map((products ?? []).map(p => [p.id, p] as const));
      return history
        .map((h): CustomerTopProduct | null => {
          const p = byId.get(h.product_id);
          if (!p) return null;
          return {
            id: p.id,
            name: p.name,
            sku: p.sku,
            ean: p.ean,
            sale_price: p.sale_price,
            stock_quantity: p.stock_quantity,
            rental_stock_quantity: p.rental_stock_quantity,
            reserved_stock_quantity: p.reserved_stock_quantity,
            product_type: p.product_type,
            sale_count: h.sale_count || 0,
          };
        })
        .filter((p): p is CustomerTopProduct => p !== null);
    },
    enabled: enabled && !!companyId && !!customerId,
    staleTime: 2 * 60 * 1000,
  });
}
